import { useState, useEffect, useMemo, Fragment } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, FolderOpen, FileText, Search, Lock, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

// Strategy files: a read-only copy of Eli's bot files (docs, settings, rule code) from the bot mirror.
// Text is shown in Inter (no monospace, per the ops design rule); nothing here can edit anything.

const READ_FONT = { fontFamily: "var(--font-num)", fontVariantNumeric: "tabular-nums" };

const fmtSize = (n) => {
  if (typeof n !== "number") return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10240 ? 1 : 0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

// ── tiny Markdown renderer (React elements only, never raw HTML) ─────

function inline(text, keyBase = "i") {
  const out = [];
  // underscores are left alone: rule names like max_daily_loss must not turn italic
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\)|\*[^*\s][^*]*\*)/g;
  let last = 0;
  let m;
  let k = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const t = m[0];
    const key = `${keyBase}-${k++}`;
    if (t.startsWith("**")) out.push(<strong key={key} className="font-semibold">{t.slice(2, -2)}</strong>);
    else if (t.startsWith("`")) out.push(<span key={key} className="px-1 rounded bg-[hsl(var(--ops-surface-2))]">{t.slice(1, -1)}</span>);
    else if (t.startsWith("[")) {
      const mm = t.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
      out.push(mm && /^https?:\/\//i.test(mm[2])
        ? <a key={key} href={mm[2]} target="_blank" rel="noopener noreferrer" className="underline text-[hsl(var(--ops-brand))]">{mm[1]}</a>
        : <span key={key}>{mm ? mm[1] : t}</span>);
    } else out.push(<em key={key}>{t.slice(1, -1)}</em>);
    last = m.index + t.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function Markdown({ text }) {
  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (/^\s*```/.test(line)) {
      const buf = [];
      i += 1;
      while (i < lines.length && !/^\s*```/.test(lines[i])) buf.push(lines[i++]);
      i += 1;
      blocks.push({ t: "pre", text: buf.join("\n") });
      continue;
    }
    const h = line.match(/^\s{0,3}(#{1,6})\s+(.*?)\s*#*\s*$/);
    if (h) { blocks.push({ t: "h", level: h[1].length, text: h[2] }); i += 1; continue; }
    if (/^\s*([-*_])(\s*\1){2,}\s*$/.test(line)) { blocks.push({ t: "hr" }); i += 1; continue; }
    if (/^\s*\|/.test(line)) {
      const rows = [];
      while (i < lines.length && /^\s*\|/.test(lines[i])) {
        const cells = lines[i].trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
        if (!cells.every((c) => /^:?-{2,}:?$/.test(c))) rows.push(cells);
        i += 1;
      }
      blocks.push({ t: "table", rows });
      continue;
    }
    if (/^\s*([-*+]|\d+[.)])\s+/.test(line)) {
      const ordered = /^\s*\d+[.)]\s+/.test(line);
      const items = [];
      while (i < lines.length && /^\s*([-*+]|\d+[.)])\s+/.test(lines[i])) {
        const depth = Math.min(3, Math.floor((lines[i].match(/^\s*/)[0].length) / 2));
        items.push({ depth, text: lines[i].replace(/^\s*([-*+]|\d+[.)])\s+/, "").replace(/^\[( |x|X)\]\s+/, (s, c) => (c.trim() ? "✓ " : "☐ ")) });
        i += 1;
        while (i < lines.length && /^\s{2,}\S/.test(lines[i]) && !/^\s*([-*+]|\d+[.)])\s+/.test(lines[i])) {
          items[items.length - 1].text += " " + lines[i].trim();
          i += 1;
        }
      }
      blocks.push({ t: ordered ? "ol" : "ul", items });
      continue;
    }
    if (/^\s*>/.test(line)) {
      const buf = [];
      while (i < lines.length && /^\s*>/.test(lines[i])) buf.push(lines[i++].replace(/^\s*>\s?/, ""));
      blocks.push({ t: "quote", text: buf.join(" ") });
      continue;
    }
    if (!line.trim()) { i += 1; continue; }
    const buf = [];
    while (i < lines.length && lines[i].trim() && !/^\s*(#{1,6}\s|```|>|\||([-*+]|\d+[.)])\s+)/.test(lines[i])) buf.push(lines[i++].trim());
    if (!buf.length) { buf.push(line.trim()); i += 1; }
    blocks.push({ t: "p", text: buf.join(" ") });
  }

  const hCls = ["", "text-lg font-semibold mt-5 mb-2", "text-base font-semibold mt-4 mb-2", "text-sm font-semibold mt-3 mb-1.5",
    "text-sm font-semibold mt-3 mb-1", "text-[13px] font-semibold mt-2 mb-1", "text-[13px] font-semibold mt-2 mb-1"];
  return (
    <div className="text-[14px] leading-relaxed text-[hsl(var(--ops-text))] break-words" style={READ_FONT}>
      {blocks.map((b, n) => {
        const key = `b${n}`;
        if (b.t === "h") { const Tag = `h${Math.min(6, b.level + 1)}`; return <Tag key={key} className={hCls[b.level]}>{inline(b.text, key)}</Tag>; }
        if (b.t === "hr") return <hr key={key} className="my-4 border-[hsl(var(--ops-border))]" />;
        if (b.t === "pre") return <pre key={key} className="my-2 p-3 rounded-md bg-[hsl(var(--ops-surface-2))] text-[13px] whitespace-pre-wrap break-words" style={{ ...READ_FONT, tabSize: 4 }}>{b.text}</pre>;
        if (b.t === "quote") return <blockquote key={key} className="my-2 pl-3 border-l-2 border-[hsl(var(--ops-brand))] text-[hsl(var(--ops-text-muted))]">{inline(b.text, key)}</blockquote>;
        if (b.t === "ul" || b.t === "ol") {
          const Tag = b.t;
          return (
            <Tag key={key} className={`my-2 space-y-1 ${b.t === "ol" ? "list-decimal" : "list-disc"} pl-5`}>
              {b.items.map((it, j) => <li key={j} style={{ marginLeft: `${it.depth * 1}rem` }}>{inline(it.text, `${key}-${j}`)}</li>)}
            </Tag>
          );
        }
        if (b.t === "table") {
          return (
            <div key={key} className="my-2 max-w-full overflow-x-auto">
              <table className="text-[13px] border-collapse">
                <tbody>
                  {b.rows.map((r, j) => (
                    <tr key={j} className="border-b border-[hsl(var(--ops-border))]">
                      {r.map((c, x) => <td key={x} className={`px-2 py-1 align-top ${j === 0 ? "font-semibold" : ""}`}>{inline(c, `${key}-${j}-${x}`)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        return <p key={key} className="my-2">{inline(b.text, key)}</p>;
      })}
    </div>
  );
}

// ── settings (JSON) as an indented key/value view ────────────────────

const KEY_WORDS = { pct: "%", qty: "quantity", max: "max", min: "min", htf: "HTF", ltf: "LTF", rr: "reward-risk",
  sl: "stop loss", tp: "take profit", atr: "ATR", ema: "EMA", rsi: "RSI", vwap: "VWAP", sim: "SIM", id: "ID",
  utc: "UTC", et: "ET", ny: "New York", mnq: "MNQ", nq: "NQ", pnl: "profit/loss", tf: "timeframe", ms: "milliseconds",
  sec: "seconds", secs: "seconds", mins: "minutes", pips: "points", pip: "points" };

const niceKey = (k) => {
  const words = String(k).replace(/([a-z])([A-Z])/g, "$1 $2").split(/[_\-\s]+/).filter(Boolean)
    .map((w) => KEY_WORDS[w.toLowerCase()] ?? w.toLowerCase());
  const s = words.join(" ");
  return s ? s[0].toUpperCase() + s.slice(1) : String(k);
};

function JsonValue({ value }) {
  if (value === null) return <span className="text-[hsl(var(--ops-text-muted))]">not set</span>;
  if (typeof value === "boolean") return <span className={value ? "ops-long" : "text-[hsl(var(--ops-text-muted))]"}>{value ? "yes" : "no"}</span>;
  if (value === "[hidden]") return <span className="inline-flex items-center gap-1 text-[hsl(var(--ops-text-muted))]"><Lock className="size-3" />hidden</span>;
  if (typeof value === "number") return <span className="font-mono-num">{value.toLocaleString("en-US", { maximumFractionDigits: 6 })}</span>;
  return <span className="break-words">{String(value)}</span>;
}

function JsonTree({ data, depth = 0 }) {
  const isArr = Array.isArray(data);
  const entries = isArr ? data.map((v, i) => [i, v]) : Object.entries(data || {});
  if (!entries.length) return <span className="text-[hsl(var(--ops-text-muted))]">{isArr ? "empty list" : "nothing set"}</span>;
  return (
    <div className={depth ? "pl-3 border-l border-[hsl(var(--ops-border))] space-y-1 mt-1" : "space-y-1.5"}>
      {entries.map(([k, v]) => {
        const nested = v && typeof v === "object";
        const label = isArr ? `#${k + 1}` : niceKey(k);
        return (
          <div key={k} className="min-w-0 text-[13px] leading-relaxed" title={isArr ? undefined : String(k)}>
            {nested ? (
              <Fragment>
                <div className="font-semibold text-[hsl(var(--ops-text))]">{label}</div>
                <JsonTree data={v} depth={depth + 1} />
              </Fragment>
            ) : (
              <div className="flex flex-wrap gap-x-2">
                <span className="text-[hsl(var(--ops-text-muted))]">{label}:</span>
                <span className="text-[hsl(var(--ops-text))] min-w-0"><JsonValue value={v} /></span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── reader ───────────────────────────────────────────────────────────

function Reader({ file, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!file) return undefined;
    let alive = true;
    setData(null); setError(null);
    api.get("/ops/strategy/file", { params: { path: file.path } })
      .then(({ data: d }) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || "That file could not be loaded right now."); });
    return () => { alive = false; };
  }, [file]);

  return (
    <Sheet open={!!file} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" data-testid="strategy-reader"
        className="w-full sm:max-w-2xl p-0 overflow-y-auto overflow-x-hidden bg-[hsl(var(--ops-bg))] border-[hsl(var(--ops-border))] text-[hsl(var(--ops-text))]">
        {file && (
          <div className="p-4 sm:p-5 space-y-3 min-w-0">
            <div className="pr-8 min-w-0">
              <SheetTitle className="text-base font-display font-semibold text-[hsl(var(--ops-text))] break-words">{file.title}</SheetTitle>
              <SheetDescription className="text-xs text-[hsl(var(--ops-text-muted))] break-words">
                {file.description}
              </SheetDescription>
              <div className="mt-1 text-[11px] text-[hsl(var(--ops-text-muted))] font-mono-num break-all">
                {file.path}{file.size != null ? ` · ${fmtSize(file.size)}` : ""}{file.copied_age_text ? ` · copied ${file.copied_age_text}` : ""}
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-[hsl(var(--ops-text-muted))] rounded-md bg-[hsl(var(--ops-surface-2))]/60 px-2 py-1.5">
              <Lock className="size-3 shrink-0" />Read-only copy of Eli's bot files. Nothing here can be changed.
            </div>
            <Card className="p-3 sm:p-4 bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] min-w-0">
              {error ? <p className="text-sm text-[hsl(var(--ops-text-muted))]">{error}</p>
                : !data ? <div className="space-y-2">{[1, 2, 3, 4].map((n) => <Skeleton key={n} className="h-4 bg-[hsl(var(--ops-surface-2))]" />)}</div>
                : data.kind === "markdown" ? <Markdown text={data.text} />
                : data.kind === "json" ? <JsonTree data={data.json} />
                : (
                  <pre data-testid="strategy-reader-text" className="text-[13px] leading-relaxed whitespace-pre-wrap break-words text-[hsl(var(--ops-text))] m-0"
                    style={{ ...READ_FONT, tabSize: 4 }}>{data.text}</pre>
                )}
            </Card>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ── the section ──────────────────────────────────────────────────────

export default function StrategyFiles() {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(null);
  const [error, setError] = useState(false);
  const [q, setQ] = useState("");
  const [file, setFile] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/ops/strategy/files")
      .then(({ data }) => { if (alive) { setList(data); setError(false); } })
      .catch(() => { if (alive) setError(true); });
    load();
    const iv = setInterval(load, 60000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  const folders = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (list?.folders || []).map((f) => ({
      ...f,
      files: needle ? f.files.filter((x) => `${x.title} ${x.description} ${x.path}`.toLowerCase().includes(needle)) : f.files,
    })).filter((f) => f.files.length);
  }, [list, q]);
  const total = (list?.folders || []).reduce((n, f) => n + f.files.length, 0);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card data-testid="ops-strategy-files" className="bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] min-w-0">
        <CollapsibleTrigger asChild>
          <button type="button" data-testid="strategy-files-toggle"
            className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-[hsl(var(--ops-surface-2))] rounded-lg transition-colors duration-150">
            <span className="flex items-center gap-2 min-w-0">
              <FolderOpen className="size-4 shrink-0 text-[hsl(var(--ops-brand))]" />
              <span className="text-sm font-semibold text-[hsl(var(--ops-text))]">Strategy files</span>
              <span className="text-[11px] text-[hsl(var(--ops-text-muted))] font-mono-num truncate">
                {list?.available ? `${total} files${list.checked_age_text ? ` · checked ${list.checked_age_text}` : ""}` : ""}
              </span>
            </span>
            <ChevronDown className={`size-4 shrink-0 text-[hsl(var(--ops-text-muted))] transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 pb-4 space-y-3 min-w-0">
            <p className="text-xs text-[hsl(var(--ops-text-muted))] leading-relaxed">
              The bot's own guides, settings and rule code, copied read-only from Eli's laptop. Tap a file to read it.
            </p>
            {error && !list && <p className="text-xs text-[hsl(var(--ops-text-muted))]">Could not load the file list right now. Retrying.</p>}
            {!list && !error && <Skeleton className="h-24 bg-[hsl(var(--ops-surface-2))]" />}
            {list?.note && (
              <p className={`flex items-start gap-1.5 text-[11px] leading-relaxed ${list.stale || !list.available ? "text-[hsl(var(--ops-warn))]" : "text-[hsl(var(--ops-text-muted))]"}`}>
                <AlertTriangle className="size-3.5 mt-px shrink-0" />{list.note}
              </p>
            )}
            {list?.available && (
              <Fragment>
                <div className="relative">
                  <Search className="size-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--ops-text-muted))]" />
                  <Input data-testid="strategy-files-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search files"
                    className="pl-8 h-8 text-xs bg-[hsl(var(--ops-surface-2))] border-[hsl(var(--ops-border))] text-[hsl(var(--ops-text))]" />
                </div>
                {!folders.length && <p className="text-xs text-[hsl(var(--ops-text-muted))]">{q ? "No file matches that search." : "No files copied yet."}</p>}
                {folders.map((f) => (
                  <div key={f.key} className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ops-text-muted))]">{f.title}</div>
                    <div className="text-[11px] text-[hsl(var(--ops-text-muted))] mb-1.5">{f.description}</div>
                    <div className="rounded-md border border-[hsl(var(--ops-border))] divide-y divide-[hsl(var(--ops-border))]">
                      {f.files.map((x) => (
                        <button key={x.path} type="button" data-testid={`strategy-file-${x.path}`} disabled={!x.available}
                          onClick={() => setFile(x)}
                          className="w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-[hsl(var(--ops-surface-2))] disabled:opacity-60 disabled:cursor-not-allowed min-w-0">
                          <FileText className="size-4 mt-0.5 shrink-0 text-[hsl(var(--ops-brand))]" />
                          <span className="min-w-0 flex-1">
                            <span className="flex flex-wrap items-baseline gap-x-2">
                              <span className="text-[13px] font-medium text-[hsl(var(--ops-text))] break-words">{x.title}</span>
                              <span className="text-[10px] text-[hsl(var(--ops-text-muted))] font-mono-num break-all">{x.path}</span>
                            </span>
                            <span className="block text-xs text-[hsl(var(--ops-text-muted))] break-words">{x.description}</span>
                            <span className="block text-[10px] text-[hsl(var(--ops-text-muted))] font-mono-num">
                              {x.available
                                ? `${fmtSize(x.size)}${x.copied_age_text ? ` · copied from Eli's laptop ${x.copied_age_text}` : ""}`
                                : "Not on the server yet"}
                            </span>
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                {list.too_large?.length > 0 && (
                  <p className="text-[11px] text-[hsl(var(--ops-text-muted))] break-words">
                    Not copied (over 1 MB): {list.too_large.map((t) => t.path).join(", ")}
                  </p>
                )}
              </Fragment>
            )}
          </div>
        </CollapsibleContent>
      </Card>
      <Reader file={file} onClose={() => setFile(null)} />
    </Collapsible>
  );
}
